import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost } from '../lib/api'
import { appendTeamMessage, markTeamChatReadLocally, teamChatKey } from '../lib/teamChat'
import type { TeamChatReadResponse, TeamChatResponse, TeamMessageResponse } from '@warcarp/api-types'

/**
 * The viewer's team thread in a team game. Drives both the thread screen and
 * the unread badges (drawer team bubble, player dots). The socket appends
 * TEAM_MESSAGE frames; a light poll covers a dropped socket. Only enable it
 * when `viewerTeamChat` says the viewer is a member, so the server is never
 * asked for a thread it would refuse.
 */
export function useTeamChat(gameId: string, enabled = true) {
  return useQuery({
    queryKey: teamChatKey(gameId),
    queryFn: () => apiGet<TeamChatResponse>(`/api/v1/games/${gameId}/team-chat`),
    enabled: !!gameId && enabled,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  })
}

export function usePostTeamMessage(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ content }: { content: string }) =>
      apiPost<TeamMessageResponse>(`/api/v1/games/${gameId}/team-chat`, { content }),
    onError: () => { void qc.invalidateQueries({ queryKey: teamChatKey(gameId) }) },
    // Dedupe with the WS echo (the server publishes before the reply flushes).
    onSuccess: (msg) => {
      qc.setQueryData<TeamChatResponse>(teamChatKey(gameId), (old) => appendTeamMessage(old, msg))
    },
  })
}

/** Mark the team thread read (clears the team bubble's badge). */
export function useMarkTeamChatRead(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiPost<TeamChatReadResponse>(`/api/v1/games/${gameId}/team-chat/read`),
    // Stamp the cache first so a close and reopen never resurrects the "NEW"
    // divider for messages already seen.
    onMutate: () => {
      qc.setQueryData<TeamChatResponse>(teamChatKey(gameId), markTeamChatReadLocally)
    },
    onSuccess: ({ lastReadAt }) => {
      qc.setQueryData<TeamChatResponse>(teamChatKey(gameId), (old) =>
        old && (old.lastReadAt === null || lastReadAt > old.lastReadAt) ? { ...old, lastReadAt } : old,
      )
    },
  })
}
